import {
    find, get, has, isEmpty, set,
} from 'lodash';

import BackendConnector from './backendConnector';
import WorkflowHttpService from './WorkflowHttpService';
import settings from '../../config/settings';

export const CANDIDATE_FEEDBACK_STATUSES = {
    NONE: 'none',
    PENDING: 'pending',
    ACCEPTED: 'accepted',
    READY_FOR_SEND: 'ready_for_send',
};

export const PROFILE_FOR_SUBMISSION_TYPE = 'p4s';
export const WORKFLOW_CREATION_DATE_LAST_DIGITS_LENGTH = 4;

export default class CandidateService {
    static async getProfile({
        requesterUserId,
        requesterUserType,
        positionId,
        workflowStep,
        talentId,
        profileType,
        visibilityMode,
        filter,
    }) {
        if (requesterUserType === 'employer') {
            const { candidate } = await BackendConnector.post(`employer/${requesterUserId}/position/${positionId}/workflowStep/${workflowStep}/${profileType}/${talentId}?visibilityMode=${visibilityMode}`, { filter, useExplain: false });

            return candidate;
        }

        return BackendConnector.get(`${requesterUserType}/${requesterUserId}/position/${positionId}/workflowStep/${workflowStep}/${profileType}/${talentId}?visibilityMode=${visibilityMode}`);
    }

    static getPreparedCandidate(candidate, talent) {
        return {
            ...candidate,
            fullName: talent?.name,
            talentID: talent?.talentId,
            currentJob: find(talent?.employmentHistory, { isCurrent: true }),
            previousJob: find(talent?.employmentHistory, { isCurrent: false }),
            predictionScore: talent?.predictionScore,
            residency: CandidateService.getLocationString(talent?.residency),
            candidateData: {
                ...talent,
                matchScore: candidate?.matchScore,
            },
        };
    }

    static getLocationString({
        city, region, state, country,
    } = {}) {
        return [city, region, state, country].filter(Boolean).join(', ');
    }

    static extendFeedbackData(candidate, feedbackProviders) {
        const feedback = get(candidate, 'workflow.feedback', null) || get(candidate, 'sample', null);
        const feedbackStatus = get(feedback, 'status', null);
        const isFeedbackProviderExist = has(feedback, 'employerRepId') || has(feedback, 'recruiterId');

        if (!isFeedbackProviderExist) {
            return candidate;
        }

        const isSampleFeedback = !isEmpty(candidate.sample);
        const isFeedbackFromEmployerExists = WorkflowHttpService.isFeedbackFromEmployerExists(feedbackStatus);
        const feedbackProviderId = feedback.employerRepId || feedback.recruiterId;
        const feedbackProviderName = feedbackProviders[feedbackProviderId];

        if (isFeedbackFromEmployerExists) {
            set(candidate, 'workflow.feedback.feedbackProviderName', feedbackProviderName);
        }

        if (isSampleFeedback) {
            set(candidate, 'sample.feedbackProviderName', feedbackProviderName);
        }

        return candidate;
    }

    static getFeedbackGrade(candidate) {
        const feedback = CandidateService.getFeedbackFromCandidate(candidate);

        return get(feedback, 'grades.0.grade', null);
    }

    static getFeedbackFromCandidate(candidate) {
        return get(candidate, 'workflow.feedback')
            || get(candidate, 'feedback')
            || get(candidate, 'sample');
    }

    static getGoodFitVisibilityState(candidate) {
        const grade = CandidateService.getFeedbackGrade(candidate);

        return grade === 1;
    }

    static getBadFitVisibilityState(candidate) {
        const grade = CandidateService.getFeedbackGrade(candidate);

        return grade === 0;
    }

    static getPendingVisibilityState(candidate) {
        const feedbackStatus = CandidateService.getFeedbackStatus(candidate);

        return feedbackStatus === CANDIDATE_FEEDBACK_STATUSES.PENDING;
    }

    static getFeedbackStatus(candidate) {
        const feedback = CandidateService.getFeedbackFromCandidate(candidate);

        return get(feedback, 'status', null);
    }

    static getFeedbackDate(candidate) {
        const feedback = CandidateService.getFeedbackFromCandidate(candidate);

        if (feedback && feedback.timestamp) {
            // eslint-disable-next-line no-undef
            const dateFormatter = new Intl.DateTimeFormat('en-US', {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
            });

            return dateFormatter.format(new Date(feedback.timestamp));
        }

        return null;
    }

    static getPublicProfileLink(candidate, { isProfileForSubmission, isNamedProfile }) {
        const talentId = get(candidate, 'workflow.talentId');
        const workflowId = get(candidate, 'workflow._id');
        const isAutoFeedbackCandidate = get(candidate, 'workflow.feedback.type') === 'automatic';

        if (isAutoFeedbackCandidate) {
            return '';
        }

        if (!talentId) {
            throw new Error('Link for public profile can\'t be generated as talentId is not defined');
        }

        if (!workflowId) {
            throw new Error('Link for public profile can\'t be generated as workflowId is not defined');
        }

        const workflow = get(candidate, 'workflow');
        const workflowDateOptionalParam = isNamedProfile ? CandidateService.getWorkflowDate(workflow) : '';
        const profileForSubmissionOptionalParam = isProfileForSubmission ? PROFILE_FOR_SUBMISSION_TYPE : '';
        const link = `${settings.TFTAppUrl}/#/public/profile/${talentId}/${workflowId}/${workflowDateOptionalParam}/${profileForSubmissionOptionalParam}`;

        return link;
    }

    static getWorkflowDate(workflow) {
        const workflowDate = get(workflow, 'created');
        const workflowDateLength = workflowDate.length;

        if (workflowDateLength >= WORKFLOW_CREATION_DATE_LAST_DIGITS_LENGTH) {
            const beginIndex = workflowDate.length - WORKFLOW_CREATION_DATE_LAST_DIGITS_LENGTH;

            return workflowDate.slice(beginIndex, workflowDateLength);
        }
        return null;
    }

    static getCandidatesContactData({ positionId, talentIds }) {
        return BackendConnector.post('/candidates/contactData', { positionId, talentIds });
    }
}
